import { useState, useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import PageSectionList from '../../../../shared/components/layouts/PageSectionList'
import PageSectionChips from '../../../../shared/components/layouts/PageSectionChips'
import FormDrawer from '../../../../shared/components/drawers/FormDrawer'
import ServiceSelectDialog from '../../../../shared/components/dialogs/ServiceSelectDialog'
import NetworkSelectDialog from '../../../../shared/components/dialogs/NetworkSelectDialog'
import PortMappingForm from './PortMappingForm'
import IngressForm from './IngressForm'
import IngressesSection from './IngressesSection'
import { useConfirmationDialog } from '../../../../system/ConfirmationDialogProvider'

function ServiceNetworkingTab({
  // Dependencies
  dependencies,
  dependenciesLoading,
  availableServices,
  createDependency,
  destroyDependency,
  // Port Mappings
  portMappings,
  portMappingsLoading,
  createPortMapping,
  updatePortMapping,
  destroyPortMapping,
  // Ingresses
  ingresses,
  ingressesLoading,
  ingressControllers,
  domains,
  projectsById,
  projectNodeIds,
  createIngress,
  updateIngress,
  destroyIngress,
  teamDataLoading,
  // Networks
  serviceNetworks,
  serviceNetworksLoading,
  availableNetworks,
  createServiceNetwork,
  destroyServiceNetwork,
  currentProjectId,
}) {
  const { openConfirmationDialog } = useConfirmationDialog()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [networkDialogOpen, setNetworkDialogOpen] = useState(false)
  const [portMappingDrawer, setPortMappingDrawer] = useState({ open: false, portMapping: null })
  const [ingressDrawer, setIngressDrawer] = useState({ open: false, ingress: null })

  // Dependency handlers
  const handleAddDependency = useCallback(() => {
    setDialogOpen(true)
  }, [])

  const handleSelectService = useCallback((selectedService) => {
    createDependency({ serviceDependency: { dependsOnServiceId: selectedService.id } })
  }, [createDependency])

  const handleDeleteDependency = useCallback((dependency) => {
    openConfirmationDialog({
      title: <FormattedMessage id="dockerServices.show.deleteDependencyTitle" />,
      message: <FormattedMessage id="dockerServices.show.deleteDependencyMessage" />,
      onConfirm: () => destroyDependency(dependency.id),
      onCancel: () => Promise.resolve(),
    })
  }, [openConfirmationDialog, destroyDependency])

  // Network handlers
  const handleAddNetwork = useCallback(() => {
    setNetworkDialogOpen(true)
  }, [])

  const handleSelectNetwork = useCallback((selectedNetwork) => {
    createServiceNetwork({ serviceNetwork: { networkId: selectedNetwork.id } })
  }, [createServiceNetwork])

  const handleDeleteNetwork = useCallback((serviceNetwork) => {
    openConfirmationDialog({
      title: <FormattedMessage id="dockerServices.show.deleteNetworkTitle" />,
      message: <FormattedMessage id="dockerServices.show.deleteNetworkMessage" />,
      onConfirm: () => destroyServiceNetwork(serviceNetwork.id),
      onCancel: () => Promise.resolve(),
    })
  }, [openConfirmationDialog, destroyServiceNetwork])

  // Port mapping handlers
  const handleOpenPortMappingDrawer = useCallback((portMapping = null) => {
    setPortMappingDrawer({ open: true, portMapping })
  }, [])

  const handleClosePortMappingDrawer = useCallback(() => {
    setPortMappingDrawer({ open: false, portMapping: null })
  }, [])

  const handleDeletePortMapping = useCallback((portMapping) => {
    openConfirmationDialog({
      title: <FormattedMessage id="portMappings.delete.title" />,
      message: <FormattedMessage id="portMappings.delete.message" />,
      onConfirm: () => destroyPortMapping(portMapping.id),
      onCancel: () => Promise.resolve(),
    })
  }, [openConfirmationDialog, destroyPortMapping])

  // Ingress handlers
  const handleOpenIngressDrawer = useCallback((ingress = null) => {
    setIngressDrawer({ open: true, ingress })
  }, [])

  const handleCloseIngressDrawer = useCallback(() => {
    setIngressDrawer({ open: false, ingress: null })
  }, [])

  const handleDeleteIngress = useCallback((ingress) => {
    openConfirmationDialog({
      title: <FormattedMessage id="ingresses.delete.title" />,
      message: <FormattedMessage id="ingresses.delete.message" />,
      onConfirm: () => destroyIngress(ingress.id),
      onCancel: () => Promise.resolve(),
    })
  }, [openConfirmationDialog, destroyIngress])

  return (
    <>
      <Typography variant="h6" sx={{ mb: 2 }}>
        <FormattedMessage id="dockerServices.sections.dependencies" />
      </Typography>
      {dependenciesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <PageSectionChips
          items={dependencies}
          emptyId="dockerServices.show.dependenciesEmpty"
          labelField="dependsOnServiceName"
          onDelete={handleDeleteDependency}
          onAdd={handleAddDependency}
          addLabelId="dockerServices.show.addDependency"
        />
      )}

      <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
        <FormattedMessage id="dockerServices.sections.networks" />
      </Typography>
      {serviceNetworksLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <PageSectionChips
          items={serviceNetworks}
          emptyId="dockerServices.show.networksEmpty"
          labelField="network.name"
          onDelete={handleDeleteNetwork}
          onAdd={handleAddNetwork}
          addLabelId="dockerServices.show.addNetwork"
        />
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, mb: 2 }}>
        <Typography variant="h6">
          <FormattedMessage id="dockerServices.sections.portMappings" />
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => handleOpenPortMappingDrawer()}
        >
          <FormattedMessage id="dockerServices.show.addPortMapping" />
        </Button>
      </Box>
      {portMappingsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <PageSectionList
          items={portMappings}
          emptyId="dockerServices.show.portMappingsEmpty"
          renderItem={(mapping) => ({
            primary: mapping.name || `Port ${mapping.sourcePort}`,
            secondary: `${mapping.targetHost}:${mapping.targetPort}:${mapping.sourcePort}`,
          })}
          onItemEdit={handleOpenPortMappingDrawer}
          onItemDelete={handleDeletePortMapping}
        />
      )}

      <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
        <FormattedMessage id="dockerServices.sections.ingresses" />
      </Typography>
      {(ingressesLoading || teamDataLoading) ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <IngressesSection
          ingresses={ingresses}
          domains={domains}
          ingressControllers={ingressControllers}
          onCreateClick={() => handleOpenIngressDrawer()}
          onEditClick={handleOpenIngressDrawer}
          onDeleteClick={handleDeleteIngress}
        />
      )}

      <ServiceSelectDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSelect={handleSelectService}
        services={availableServices}
        titleId="dockerServices.show.selectDependencyTitle"
        emptyId="dockerServices.show.noAvailableServices"
      />

      <NetworkSelectDialog
        open={networkDialogOpen}
        onClose={() => setNetworkDialogOpen(false)}
        onSelect={handleSelectNetwork}
        networks={availableNetworks}
        projectsById={projectsById}
        currentProjectId={currentProjectId}
        titleId="dockerServices.show.selectNetworkTitle"
        emptyId="dockerServices.show.noAvailableNetworks"
      />

      <FormDrawer
        open={portMappingDrawer.open}
        onClose={handleClosePortMappingDrawer}
        titleId={portMappingDrawer.portMapping ? 'portMappings.edit.title' : 'portMappings.create.title'}
      >
        <PortMappingForm
          portMapping={portMappingDrawer.portMapping}
          createPortMapping={createPortMapping}
          updatePortMapping={updatePortMapping}
          onSuccess={handleClosePortMappingDrawer}
          onCancel={handleClosePortMappingDrawer}
        />
      </FormDrawer>

      <FormDrawer
        open={ingressDrawer.open}
        onClose={handleCloseIngressDrawer}
        titleId={ingressDrawer.ingress ? 'ingresses.edit.title' : 'ingresses.create.title'}
      >
        <IngressForm
          ingress={ingressDrawer.ingress}
          ingressControllers={ingressControllers}
          domains={domains}
          projectNodeIds={projectNodeIds}
          projectsById={projectsById}
          createIngress={createIngress}
          updateIngress={updateIngress}
          onSuccess={handleCloseIngressDrawer}
          onCancel={handleCloseIngressDrawer}
        />
      </FormDrawer>
    </>
  )
}

export default ServiceNetworkingTab
